import { useCallback, useContext, useMemo, useState, createContext, ReactElement, useEffect } from "react";
import { GameComponentForGame, SetVariableData } from "../implementationTypes";
import { TableContext, TableContextType } from "./tableContext";
import { useParams } from "react-router-dom";
import { gameSituationContext, GameSituationContextType } from "./GameSituationContext";
import {
  BankComp,
  CardWrap,
  ClickableArea,
  ComponentPosition,
  DeckComp,
  LabelDisplayComp,
} from "./GameComponentElements";
import { createPortal } from "react-dom";
import { initialSetup } from "../implementationGlobals";
import { Tooltip } from "@mui/material";
import CgoCardSide from "../CgoCard";
import { isWebkit } from "./tableUtility";
import { GridPositionProps } from "../types";
import { substituteIconsInText } from "../cardTextIcons";
import { Asset } from "../types";

export function ComponentComp(props: {
  data?: any;
  x?: number;
  y?: number;
  height?: number;
  width?: number;
  tooltipWrapRef?: React.MutableRefObject<HTMLElement | null>;
  scaleMult?: number;
  positionScale?: number;
  positionHoverScale?: number;
  parentScale?: number;
  preview?: boolean;
  displayMode?: string;
  playerColor?: string;
  transform?: string;
  positionZIndex?: number;
  isPosSelectable?: boolean;
  clickable?: boolean | string;
  logPreview?: boolean;
  selectedComponent?: GameComponentForGame;
  situationScale?: number;
  deckTopCard?: boolean;
  deckContentDisplay?: boolean;
  deckDisplayDiscard?: boolean;
}) {
  const [contextOpen, setContextOpen] = useState<number[] | false>(false);
  const [fullRotations, setFullRotations] = useState(0);
  const [isZoomed, setIsZoomed] = useState<boolean>(false);
  const [isHovered, setIsHovered] = useState<boolean>(false);
  let {
    isClickable,
    customTooltip,
    tableState,
    workingTableStateRef,
    getPcCoords,
    getComponentInfo,
    adjustedGameState,
    playAsColor,
    decks,
    setEditMode: _setEditMode,
    evalSpeed,
    getImage,
    isPosVisible,
    situationScale,
    editMode,
    cheatMode,
    globalAssets,
    eventManager,
    tableStatus,
    projectFonts,
    deckCardFonts,
  } = useContext(TableContext as React.Context<TableContextType>);
  let {
    componentClickCallBack,
    getSelectedComponent,
    optionSelectCallBack,
    discardComponentCallBack,
    positionClickCallBack,
    bankReveal,
    addGameIconsToText,
    macroClickCallBack,
    displayTooltips,
  } = props.logPreview
    ? {
        componentClickCallBack: (component: GameComponentForGame, evt: React.MouseEvent<Element, MouseEvent>) =>
          undefined,
        getSelectedComponent: () => null,
        optionSelectCallBack: (options: SetVariableData, evt: React.ChangeEvent<Element>) => {},
        discardComponentCallBack: (
          componentData: GameComponentForGame,
          destination: string,
          evt: React.MouseEvent<Element, MouseEvent>,
        ) => {},
        positionClickCallBack: (
          destinationId: string,
          positionIndex: string | number,
          positionName: string,
          visible: any,
          evt: React.MouseEvent<Element, MouseEvent>,
        ) => undefined,
        bankReveal: undefined,
        addGameIconsToText: () => "",
        displayTooltips: true,
        macroClickCallBack: (
          name: string,
          options: {
            [key: string]: any;
          },
          buttonData: {
            [key: string]: any;
          },
        ) => {},
      }
    : useContext(gameSituationContext as React.Context<GameSituationContextType>);
  const params = useParams();
  if (!props.data) {
    return <div>loading..</div>;
  }
  if (!getComponentInfo) {
    debugger;
  }
  let componentInfo = useMemo(() => {
    return getComponentInfo(props.data);
  }, [props.data, tableState.getActiveState(), getComponentInfo]);
  const [expandedPositions, setExpandedPositions] = useState(new Set());
  const [animationPlayed, setAnimationPlayed] = useState(false);

  let children = [];

  let [xDim, yDim] = [100, 100];
  if (componentInfo?.positions?.dims) {
    [xDim, yDim] = componentInfo.positions.dims;
  }

  const hoverCallBack = useCallback(
    (evt: React.MouseEvent) => {
      let target = evt.target as HTMLElement;
      if (componentInfo.hoverZoom || props.positionHoverScale || props.deckContentDisplay) {
        setIsZoomed(true);
      }
      if (!target.classList.contains("root")) {
        setIsHovered(true);
      }
      evt.stopPropagation();
    },
    [componentInfo],
  );
  const leaveCallBack = useCallback((evt: React.MouseEvent) => {
    setIsZoomed(false);
    let target = evt.target as HTMLElement;
    setIsHovered(false);
    if (!target.parentElement?.classList.contains("root")) {
      //evt.target.parentElement.classList.add("hovering");
    }
    evt.stopPropagation();
  }, []);
  useEffect(() => {
    setIsHovered(false);
    setIsZoomed(false);
  }, [props.data.childData]);
  function availableGridCoords(
    gridPositionData: GridPositionProps,
    children: { [key: string]: GameComponentForGame } = {},
  ) {
    let result: { [key: string]: { x: number; y: number } } = {};
    let occupiedCoords = Object.keys(children);
    if (occupiedCoords.length === 0 && gridPositionData.flexible) {
      return { "0:0": { x: 0, y: 0 } };
    }
    let occupiedCoordsObj = occupiedCoords.map((c) => c.split(":").map((n) => +n));

    let maxX = Math.max(...occupiedCoordsObj.map((c) => c[0]));
    let maxY = Math.max(...occupiedCoordsObj.map((c) => c[1]));
    let startX = Math.min(...occupiedCoordsObj.map((c) => c[0]));
    let startY = Math.min(...occupiedCoordsObj.map((c) => c[1]));
    let occupiedWidth = maxX - startX + 1;
    let occupiedHeight = maxY - startY + 1;
    let availWidth = gridPositionData.maxCols - occupiedWidth;
    let availHeight = gridPositionData.maxRows - occupiedHeight;

    maxX += availWidth;
    startX -= availWidth;

    maxY += availHeight;
    startY -= availHeight;

    if (!gridPositionData.flexible) {
      startX = startY = 0;
      maxX = gridPositionData.maxCols - 1;
      maxY = gridPositionData.maxRows - 1;
    }
    for (let y = startY; y <= maxY; ++y) {
      for (let x = startX; x <= maxX; ++x) {
        let ok = false;
        if (x === 0 && y === 0) {
          ok = true;
        }
        if (gridPositionData.onlyAdjacent) {
          let dirs = [
            [-1, 0],
            [1, 0],
            [0, -1],
            [0, 1],
          ];
          for (let [dx, dy] of dirs) {
            if (occupiedCoords.includes(x + dx + ":" + (y + dy))) {
              ok = true;
            }
          }
        } else {
          ok = true;
        }
        if (occupiedCoords.includes(x + ":" + y)) {
          ok = false;
        }
        if (ok) {
          result[x + ":" + y] = { x, y };
        }
      }
    }
    return result;
  }

  let maxHeight = (props.height ?? 0) - 30;
  let width = +componentInfo?.width as number | undefined;
  if (typeof width === "number" && isNaN(width)) {
    width = undefined;
  }
  let height = +componentInfo?.height as number | undefined;
  if (typeof height === "number" && isNaN(height)) {
    height = undefined;
  }
  let targetScale = useMemo(() => {
    let cardScale: any;
    let result = 1;
    if (props.width || props.height) {
      let baseWidth = componentInfo.width;
      let baseHeight = componentInfo.height;
      let targetWidth = (props.width ?? 0) / baseWidth;
      let targetHeight = (props.height ?? 0) / baseHeight;
      let componentScale = componentInfo.scaleMult ?? 1;
      if (!targetWidth) {
        return targetHeight * componentScale;
      }
      if (!targetHeight) {
        return targetWidth * componentScale;
      }
      return Math.min(targetWidth, targetHeight) * componentScale;
    }
    if (props.data.options?.deckName) {
      cardScale = (window.componentPool.baseScale ?? 1) * (props.scaleMult || 1) * (props.positionScale || 1);

      result = cardScale;
    } else {
      if (props.scaleMult && props.scaleMult !== 1) {
        result *= props.scaleMult;
      }
    }

    if (props.preview) {
      let maxWidth = (props.width ?? 0) - 30;
      result = 100;
      if (height && height * result > maxHeight) {
        result = maxHeight / height;
      }
      if (width && width * result > maxWidth) {
        result = maxWidth / width;
      }
      if (props.data.options?.deckName) {
        result = cardScale ?? 1;
      }
    }
    if (componentInfo.scaleMult && props.data.name.includes("__card")) {
      result *= componentInfo.scaleMult;
    }

    return result;
  }, [props.width, props.scaleMult, props.parentScale, props.positionScale, props.data, componentInfo]);
  let targetTransform = "translate(0, 0) scale(" + targetScale + ")";

  let baseDisplayMode = componentInfo?.displayMode || props.displayMode;

  if (componentInfo.hidden) {
    return <></>;
  }

  let isOwnComponent = playAsColor === "alwaysactive" || playAsColor === props.playerColor;
  if (!props.playerColor) {
    isOwnComponent = playAsColor === tableState.getActiveState().activePlayer || playAsColor === "alwaysactive";
  }
  if (isOwnComponent) {
    if (baseDisplayMode === "opponentCardBack") {
      baseDisplayMode = undefined;
    }
  } else {
    if (baseDisplayMode === "owningPlayerOnly") {
      return <></>;
    }
  }
  for (let positionName of new Set([
    ...Object.keys(props.data.children ?? {}),
    ...Object.keys(componentInfo?.positions ?? {}),
  ])) {
    let positions = componentInfo?.positions ?? {};
    let constantComponents = positions[positionName]?.constantComponents ?? {};
    let childPosNames = Object.keys((props.data.children ?? {})[positionName] ?? {});
    if (constantComponents) {
      for (let i in constantComponents) {
        if (!childPosNames.includes(i)) {
          childPosNames.push(i);
        }
      }
    }
    if (positionName === "hidden" || props.preview) {
      continue;
    }
    for (let childPos of childPosNames) {
      let displayMode = baseDisplayMode;

      let positionScale = 1;
      if (positions) {
        positionScale *= positions[positionName]?.scaleMult ?? 1;
        if (positions[positionName]?.coords && positionName !== "unpositioned") {
          let indexScale = positions[positionName]?.coords[childPos]?.scaleMult;
          positionScale *= indexScale ?? 1;
        }
      }
      if (positions[positionName] && positions[positionName].coords) {
        displayMode =
          positions[positionName].coords[childPos]?.displayMode ?? positions[positionName]?.displayMode ?? displayMode;
      }
      if (isOwnComponent && displayMode === "opponentCardBack") {
        displayMode = undefined;
      }
      if (positions && positions[positionName]?.multi) {
        let positionInfo = positions[positionName];
        if (positionInfo.collapse) {
          let [xDim, yDim] = [100, 100];
          if (componentInfo?.positions?.dims) {
            [xDim, yDim] = componentInfo.positions.dims;
          }
          let { x: srcX, y: srcY } = positionInfo.collapse.buttonCoords;
          let resultX = (srcX / xDim) * 100;
          let resultY = (srcY / yDim) * 100;
          let button = (
            <button
              className="collapse-button"
              key={"collapse-button-" + positionName + "-" + props.data.id}
              style={{
                left: resultX + "%",
                top: resultY + "%",
              }}
              onClick={() => {
                setExpandedPositions((oldPositions) => {
                  let newPositions = new Set(oldPositions);
                  if (newPositions.has(positionName)) {
                    newPositions.delete(positionName);
                  } else {
                    newPositions.add(positionName);
                  }
                  return newPositions;
                });
              }}
            >
              {expandedPositions.has(positionName)
                ? (positionInfo.collapse.collapseLabel ?? positionInfo.collapse.label ?? positionName)
                : (positionInfo.collapse.label ?? positionName)}
            </button>
          );
          children.push(button);
          if (!expandedPositions.has(positionName)) {
            continue;
          }
        }
        let coords = positionInfo.coords ?? {};
        let multiOverload = coords[childPos]?.multiOverload ?? [];
        if (!props.data.children[positionName][childPos]?.filter) {
          continue;
        }
        let childList = props.data.children[positionName][childPos].filter((a: any) => a);
        childList.forEach((a: any) => (a.key = a.id));

        let gapSize = positionInfo.gapSize || 60; // TODO? defined in components
        if (positionInfo.maxSpread && gapSize * (childList.length - 1) > positionInfo.maxSpread) {
          gapSize = positionInfo.maxSpread / (childList.length - 1);
        }
        for (
          let i = positionInfo.displayLastOnly ? childList.length - 1 : 0;
          i < childList.length && (!positionInfo.positionHidden || editMode);
          ++i
        ) {
          let completePositionInfo = Object.assign({}, positionInfo, coords[childPos] ?? {}, multiOverload[i] ?? {});
          let finalPositionScale = completePositionInfo.scaleMult ?? positionScale;
          let child = childList[i];

          let childCoords = getPcCoords(props.data, positionName, childPos, +i, childList, child, componentInfo);
          let componentData = Object.assign({ childData: { positionName, childPos, i } }, child);
          if (!componentData.id) {
            continue;
          }
          children.push(
            <ComponentComp
              data={componentData}
              x={childCoords[0] /*sx + dx*i*/ /*todo*/}
              y={childCoords[1] /*sy + dy*i*/}
              transform={completePositionInfo.transform ?? ""}
              displayMode={displayMode}
              scaleMult={(componentInfo?.scaleMult ?? 1) * (finalPositionScale || 1)}
              width={((completePositionInfo.scaleWidth ?? 0) / componentInfo.positions?.dims?.[0]) * (width ?? 0)}
              height={((completePositionInfo.scaleHeight ?? 0) / componentInfo.positions?.dims?.[1]) * (height ?? 0)}
              positionScale={finalPositionScale || 1}
              positionHoverScale={completePositionInfo.hoverZoom}
              positionZIndex={positionInfo.zIndex}
              isPosSelectable={positions[positionName]?.selectable}
              key={componentData.id}
              playerColor={props.playerColor}
              parentScale={(props.parentScale || 1) * targetScale}
              tooltipWrapRef={props.tooltipWrapRef}
            />,
          );
        }
      } else {
        let child = (props.data.children[positionName] ?? {})[childPos] as GameComponentForGame;
        let constantComponent = (positions[positionName]?.constantComponents ?? {})[childPos];
        if (constantComponent) {
          child = constantComponent;
        }
        let x = 0,
          y = 0;
        if (!child) {
          continue;
        }
        let pcCoords = getPcCoords(props.data, positionName, childPos, 0, [child], child, componentInfo);
        if (!Array.isArray(pcCoords)) {
          debugger;
        }
        [x, y] = pcCoords;
        let componentData = Object.assign({}, child, {
          childData: { positionName, childPos },
        });
        children.push(
          <ComponentComp
            data={componentData}
            x={x}
            y={y}
            transform={
              componentInfo.positions ? componentInfo.positions[positionName]?.coords?.[childPos]?.transform : ""
            }
            displayMode={displayMode}
            playerColor={props.playerColor}
            scaleMult={(componentInfo?.scaleMult || 1) * (positionScale || 1)}
            positionScale={positionScale || 1}
            positionHoverScale={componentInfo.positions && componentInfo.positions[positionName]?.hoverZoom}
            positionZIndex={componentInfo.positions && componentInfo.positions[positionName]?.zIndex}
            key={child.id + positionName + childPos}
            isPosSelectable={positions && positions[positionName] && positions[positionName]?.selectable}
            width={((positions[positionName]?.scaleWidth ?? 0) / componentInfo.positions?.dims?.[0]) * (width ?? 0)}
            height={((positions[positionName]?.scaleHeight ?? 0) / componentInfo.positions?.dims?.[1]) * (height ?? 0)}
            parentScale={(props.parentScale || 1) * targetScale}
            tooltipWrapRef={props.tooltipWrapRef}
          />,
        );
      }
    }
  }

  if (componentInfo?.buttons && !props.logPreview) {
    for (let buttonName in componentInfo.buttons) {
      let buttonData = componentInfo.buttons[buttonName];
      if (buttonData.onlyEditMode && !editMode) {
        continue;
      }
      let [x, y] = [(buttonData.x / xDim) * 100, (buttonData.y / yDim) * 100];
      children.push(
        <button
          style={{
            left: x + "%",
            top: y + "%",
          }}
          key={buttonName}
          className="macro-button"
          onClick={macroClickCallBack.bind(
            null,
            buttonName,
            { player: props.playerColor, parent: props.data.id },
            buttonData,
          )}
        >
          {buttonData.text}
        </button>,
      );
    }
  }
  let childrenSet = new Set(children.map((c: any) => c.key));
  if (childrenSet.size !== children.length) {
    //throw "Duplicate id?"
  }
  if (children.find((c: any) => c.key === undefined)) {
    throw "missing id?";
  }
  const positionElements = useMemo(() => {
    if (tableStatus === "ended") {
      return [];
    }
    let result = [];
    for (let positionName of Object.keys(componentInfo?.positions || {})) {
      if (positionName === "dims" || props.preview) {
        continue;
      }

      let coords = componentInfo.positions[positionName]?.coords || {};
      //if (positions.accepts.includes(props.selectedComponent.name)) {
      //https://stackoverflow.com/questions/3629817/getting-a-union-of-two-arrays-in-javascript
      if (componentInfo.positions[positionName]?.gridCoords) {
        coords = availableGridCoords(
          componentInfo.positions[positionName]?.gridCoords,
          props.data.children[positionName],
        );
      }

      //if (positions.accepts.includes(props.selectedComponent.name)) {
      //https://stackoverflow.com/questions/3629817/getting-a-union-of-two-arrays-in-javascript

      if (typeof coords !== "object") {
        debugger;
      }
      for (let positionIndex of Object.keys(coords)) {
        if (!props.data.children) {
          debugger;
          break;
        }

        let [x, y] = getPcCoords(
          props.data,
          positionName,
          positionIndex,
          undefined,
          undefined,
          undefined,
          componentInfo,
        );
        result.push(
          <ComponentPosition
            x={x}
            y={y}
            parentId={props.data.id}
            name={positionName}
            index={positionIndex}
            posData={props.data.children[positionName]}
            parentScale={(props.parentScale || 1) * targetScale}
            key={"pos-" + props.data.id + "-" + positionName + "-" + positionIndex}
            parentData={props.data}
            parentInfo={componentInfo}
            logPreview={!!props.logPreview}
          />,
        );
      }
    }
    return result;
  }, [componentInfo?.positions, tableStatus]);

  let innerElement: ReactElement[] | ReactElement = children.concat(positionElements);
  let backgroundImage = getImage(componentInfo?.image);

  let componentDisplayStyle: { [key: string]: any } = Object.assign(
    {
      backgroundImage: backgroundImage,
      backgroundColor: backgroundImage,
      width: "100%",
      height: "100%",
      filter: componentInfo.filter,
      //zIndex: props.positionZIndex ?? componentInfo.zIndex,
      clipPath: componentInfo.clipPath,
    },
    {},
  );

  //componentInfo?.style ||
  const componentSelectable = useMemo(() => {
    let result = true;
    if (componentInfo?.selectable !== undefined) {
      result = componentInfo?.selectable;
    }
    if (editMode && componentInfo?.editModeSelectable !== undefined) {
      result = componentInfo?.editModeSelectable;
    }
    if (props.deckTopCard) {
      result = false;
    }
    if (props.isPosSelectable === false) {
      result = false;
    }
    return result;
  }, [componentInfo?.selectable, editMode, componentInfo.editModeSelectable, props.deckTopCard]);

  let componentType = componentInfo?.type || props.data.type;
  const isCard = props.data.options?.deckName;
  const domId = "wrap-" + props.data.id;
  const innerStyle: { [key: string]: any } = useMemo(() => {
    let innerStyle = {} as { [key: string]: any };
    if (!isWebkit()) {
      innerStyle.filter = "";
      if (componentType === "token") {
        innerStyle.filter = "drop-shadow(-3px 1px 1px black)";
      }
      if (componentType === "board") {
        innerStyle.filter = "drop-shadow(2px 1px 4px black);";
      }
      if (componentInfo.filter && !isCard) {
        innerStyle.filter += " " + componentInfo.filter;
      }
    }
    if (props.deckDisplayDiscard) {
      innerStyle.filter += " grayscale(0.4)";
    }
    //innerStyle.clipPath = componentInfo.clipPath;
    if (componentSelectable) {
      innerStyle["--pulse-width"] = 5 / targetScale + "px";
    }
    if (getSelectedComponent && getSelectedComponent()?.id === props.data.id && props.data.id) {
      if (!isWebkit() && !style.filter) {
        innerStyle.filter = "drop-shadow(0px 0px 10px rgb(255, 255, 174))";
      }
    }
    let innerTransform = "";
    if (componentInfo?.transform) {
      innerTransform += " " + (componentInfo?.transform || "");
    }
    if (props.transform) {
      innerTransform += props.transform;
    }
    if (isZoomed && (componentInfo.hoverZoom || props.positionHoverScale || props.deckContentDisplay)) {
      let compElement = document.getElementById(domId);
      let bounds = compElement?.getBoundingClientRect();
      let targetZoom = props.deckContentDisplay
        ? 1.1
        : (componentInfo.hoverZoom ?? 1) * (props.positionHoverScale ?? 1);
      innerTransform += "scale(" + targetZoom + ")";
      if (bounds && targetZoom) {
        let topDestination = (bounds.top + bounds.bottom) / 2 - bounds.height * 0.5 * targetZoom;
        let bottomDestination = (bounds.top + bounds.bottom) / 2 + bounds.height * 0.5 * targetZoom;
        let leftDestination = (bounds.left + bounds.right) / 2 - bounds.width * 0.5 * targetZoom;
        let rightDestination = (bounds.left + bounds.right) / 2 + bounds.width * 0.5 * targetZoom;
        let factor = parseFloat(compElement?.style.width ?? "0") / bounds.width;
        if (topDestination < 80) {
          innerTransform += " translate(0, " + ((-topDestination + 80) * factor) / targetZoom + "px)";
        }
        if (bottomDestination > window.innerHeight - 10) {
          innerTransform +=
            " translate(0, " + ((-bottomDestination + window.innerHeight - 10) * factor) / targetZoom + "px)";
        }
        if (leftDestination < 10) {
          innerTransform += " translate(" + ((-leftDestination + 10) * factor) / targetZoom + "px, 0)";
        }
        if (rightDestination > window.innerWidth - 10) {
          innerTransform +=
            " translate(" + ((-rightDestination + window.innerWidth - 10) * factor) / targetZoom + "px, 0)";
        }
      }
    }

    let oldDiv = document.getElementById(domId)?.querySelector(".component-inner") as HTMLElement;
    let oldTransform = oldDiv?.style?.transform;
    if (oldTransform) {
      let oldRotation: any[] | null = oldTransform.match(/rotate\((-?\d+(\.\d+)?)deg\)/);
      if (!oldRotation) {
        oldRotation = [null, 0];
      }
      let newRotation = innerTransform.match(/rotate\((-?\d+(\.\d+)?)deg\)/);
      if (newRotation) {
        let oldRotAmt = +oldRotation[1];
        let newRotAmt = +newRotation[1] + 360 * (fullRotations ?? 0);
        let rotationDistance = oldRotAmt - newRotAmt;
        let addFullRotations = Math.round(rotationDistance / 360);
        if (addFullRotations !== 0) {
          setFullRotations((r) => r + addFullRotations);
        }
        innerTransform = innerTransform.replace(/(-?\d+(\.\d+)?)deg/, newRotAmt + addFullRotations * 360 + "deg");
      }
    }
    innerStyle.transform = innerTransform;
    return innerStyle;
  }, [
    isZoomed,
    props.data.id,
    componentInfo?.type,
    props.positionHoverScale,
    componentInfo.hoverZoom,
    componentInfo.filter,
    props.transform,
    componentInfo?.transform,
    domId,
    componentInfo?.hoverZoom,
    props.deckContentDisplay,
    fullRotations,
    componentSelectable,
    getSelectedComponent,
    props.data.id,
    targetScale,
    isCard,
  ]);

  let iconData = (tableState?.dataSheet ?? {})[props.data.name + "__icons"];
  let icons = [];
  let newIcons: { [key: string]: Asset } = {};
  for (let name in globalAssets) {
    newIcons[name] = { url: globalAssets[name].url, aspect: 1 };
  }
  if (iconData) {
    for (let row = 1; row < iconData.length; ++row) {
      let iconizedText = substituteIconsInText(
        iconData[row][iconData[0].indexOf("text")],
        iconData[row][iconData[0].indexOf("fontSize")],
        newIcons,
      );
      let newEl = (
        <div
          className="component-icons-wrap"
          style={{
            left: iconData[row][iconData[0].indexOf("x")] + "px",
            top: iconData[row][iconData[0].indexOf("y")] + "px",
            fontSize: iconData[row][iconData[0].indexOf("fontSize")],
          }}
        >
          {iconizedText}
        </div>
      );

      icons.push(newEl);
    }
  }

  let componentDisplay = (
    <div className="component-display" style={componentDisplayStyle} key={"component-display"}>
      {icons}
    </div>
  );

  let dimensionStyle: { [key: string]: any } = {};
  let deckInfo: any = {};
  let cardRevStyle: { [key: string]: any } = {};
  let revImageName: string | undefined = "";
  if (isCard) {
    deckInfo = decks[props.data.options.deckName];

    dimensionStyle = {
      width: "100%",
      height: "100%",
    };
    let borderRadius = componentInfo.borderRadius ?? "37px";

    let cardData = componentInfo;
    if (props.data.options.revImageName) {
      revImageName = getImage(props.data.options.revImageName);
    }
    if (getImage && cardData?.cardBack && cardData.cardBack !== "__componentDataFallback") {
      revImageName = getImage(cardData.cardBack);
    }
    cardRevStyle = {
      backgroundImage: revImageName,
      backgroundColor: "",
      borderRadius,
    };

    if (!revImageName || revImageName === "url()") {
      cardRevStyle.backgroundColor = "#dddddd";
    }
  }

  const cardRev = useMemo(() => {
    if (!isCard) {
      return undefined;
    }
    let cardRev = <div className="card-back" style={Object.assign({}, dimensionStyle, cardRevStyle)}></div>;
    if (props.data.options.revCard?.cardId) {
      let revDeckName = props.data.options.revDeckName ?? props.data.options.deckName;
      cardRev = (
        <CgoCardSide
          cardId={props.data.options.revCardId}
          deckInfo={decks[revDeckName]}
          globalAssets={globalAssets}
          hiddenLayers={componentInfo.hiddenLayers}
          style={{
            borderRadius: componentInfo.borderRadius ?? "37px",
            filter: componentInfo.filter ? componentInfo.filter : undefined,
          }}
          projectFonts={projectFonts}
          deckFontOverrides={deckCardFonts?.filter((font) => font.deckName === revDeckName && font.cardName == null)}
          cardFontOverrides={deckCardFonts?.filter(
            (font) => font.deckName === revDeckName && font.cardName === props.data.options.cardId,
          )}
        ></CgoCardSide>
      );
    }
    if (deckInfo?.rulesRev) {
      cardRev = (
        <CgoCardSide
          cardId={props.data.options.cardId ?? props.data.options.id} // second part for bw comp when cardid side was just id
          deckInfo={deckInfo}
          globalAssets={globalAssets}
          hiddenLayers={componentInfo.hiddenLayers}
          isRev={true}
          style={{
            borderRadius: componentInfo.borderRadius ?? "37px",
            filter: componentInfo.filter ? componentInfo.filter : undefined,
          }}
          projectFonts={projectFonts}
          deckFontOverrides={deckCardFonts?.filter(
            (font) => font.deckName === props.data.options.revDeckName && font.cardName == null,
          )}
          cardFontOverrides={deckCardFonts?.filter(
            (font) => font.deckName === props.data.options.revDeckName && font.cardName === props.data.options.cardId,
          )}
        ></CgoCardSide>
      );
    }
    return cardRev;
  }, [
    props.data.options?.revCardId,
    props.data.options?.cardId,
    props.data.options?.revDeckName,
    props.data.options?.deckName,
    deckInfo,
    projectFonts,
    deckCardFonts,
    componentInfo.filter,
  ]);

  let cardFront = useMemo(() => {
    if (!isCard) {
      return undefined;
    }
    return (
      <CgoCardSide
        cardId={props.data.options.cardId ?? props.data.options.id} // second part for bw comp when cardid side was just id
        deckInfo={deckInfo}
        globalAssets={globalAssets}
        hiddenLayers={componentInfo.hiddenLayers}
        projectFonts={projectFonts}
        deckFontOverrides={deckCardFonts?.filter(
          (font) => font.deckName === props.data.options.deckName && font.cardName == null,
        )}
        cardFontOverrides={deckCardFonts?.filter(
          (font) => font.deckName === props.data.options.deckName && font.cardName === props.data.options.cardId,
        )}
        style={{ filter: componentInfo.filter ? componentInfo.filter : undefined }}
      ></CgoCardSide>
    );
  }, [props.data.options?.cardId, props.data.options?.id, deckInfo, projectFonts, deckCardFonts, componentInfo.filter]);
  if (isCard) {
    let cardFlipped =
      baseDisplayMode === "opponentCardBack" || baseDisplayMode === "cardBack" || props.data.options.cardFlipped;
    if (componentInfo.forceVisible) {
      cardFlipped = false;
    }
    if ((props.data.options.cardUnflippedFor ?? []).includes(playAsColor)) {
      cardFlipped = false;
    }
    let cardWrap = (
      <CardWrap
        cardFront={cardFront}
        cardRev={cardRev}
        cardBackImage={revImageName}
        flipped={cardFlipped}
        borderRadius={componentInfo.borderRadius}
        clipPath={componentInfo?.clipPath}
      >
        {children}
      </CardWrap>
    );

    //innerElement.unshift(cardWrap);
    componentDisplay = cardWrap;

    width = deckInfo?.cardInfo.w;
    height = deckInfo?.cardInfo.h;
  }

  let xPc = props.x || 0;
  let yPc = props.y || 0;

  let hidden = baseDisplayMode == "editModeOnly" && !editMode;

  switch (props.data.type) {
    case "deck":
      let topCard = tableState?.containers[props.data.id].deckTopCard;
      if (topCard) {
        topCard.options.cardFlipped = true;
        if (!props.data.options.facedown) {
          topCard.options.cardFlipped = false;
        }
      }

      innerElement = (
        <DeckComp
          deckName={props.data.name}
          data={props.data}
          height={190}
          cardBack={getImage(
            getComponentInfo({
              name: "__card-" + props.data.deckName,
              options: {},
              id: "dummy",
              children: {},
            })?.cardBack,
          )}
          componentAmt={tableState?.containers[props.data.id].componentAmt}
          discardAmt={tableState?.containers[props.data.id].discardSize}
          topCard={
            props.displayMode === "opponentCardBack" ? undefined : tableState?.containers[props.data.id].deckTopCard
          }
        />
      );
      height = 190;
      width = 190 * 0.7;
      break;
    case "bank":
      innerElement = (
        <BankComp
          name={props.data.name}
          canMove={props.data.options.contentNames.includes(props.selectedComponent?.name)}
        ></BankComp>
      );
      break;
    case "var-display":
      innerElement = (
        <LabelDisplayComp
          name={props.data.name}
          value={props.data.value}
          options={Object.assign({ displayVariableName: true }, props.data.options)}
        ></LabelDisplayComp>
      );
      break;
    default:
      break;
  }

  let leftCss = "calc(" + xPc + "% - " + (width || 0) / 2 + "px)";
  let topCss = "calc(" + yPc + "% - " + (height || 0) / 2 + "px)";
  if (props.data.name === "root") {
    leftCss = "0";
    topCss = "0";
  }
  let innerClassName = "component-inner " + props.data.name;

  let wrapClassName = "component-wrap cgo-component type-" + componentType;
  let clickable = useMemo(() => {
    if (tableStatus === "ended") {
      return false;
    }
    return !props.deckTopCard && isClickable(props.data);
  }, [props.data, editMode, playAsColor, tableState, tableStatus]);
  let style: { [key: string]: any } = useMemo(() => {
    let result: { [key: string]: any } = {
      left: leftCss,
      top: topCss,
      width: width,
      height: height,
      zIndex: (props.positionZIndex ?? componentInfo.zIndex ?? 0) + (isZoomed && isHovered ? 1000 : 0),
      //clipPath: componentInfo.clipPath
    };
    if (hidden) {
      result.opacity = 0;
      result.pointerEvents = "none";
    }
    result.transform = targetTransform;
    if (props.data.animationData && Date.now() - props.data.animationData.timestamp < 50) {
      result.display = "none";
    }
    if (props.logPreview) {
      result.zIndex = 60;
    }
    if (clickable) {
      result.zIndex += 1600;
    }
    if (props.data.animationHide) {
      result.display = "none";
    }
    return result;
  }, [
    props.clickable,
    props.positionZIndex,
    componentInfo,
    isZoomed,
    props.data.animationData,
    targetScale,
    props.x,
    props.y,
    props.logPreview,
    props.data.animationHide,
  ]);

  let windowWidth = decks[props.data.options?.deckName]?.cardInfo.w + 100 || 400;
  let windowHeight = decks[props.data.options?.deckName]?.cardInfo.h + 200 || 350;
  let scaleMult = componentInfo.tooltipScaleMult ?? 1;
  windowHeight *= scaleMult;
  windowWidth *= scaleMult;
  let componentName = props.data.name;
  let variables = window.componentPool[componentName]?.variables || [];
  let tooltipElement = useMemo(() => {
    if (contextOpen && !props.logPreview) {
      let gameSituationNode = document.querySelector(".game-situation") as HTMLElement;
      wrapClassName += " context-open";
      let options = [];
      let allVariables = { ...variables };
      if (componentName.startsWith("__card")) {
        allVariables.cardId = Object.keys(tableState.deckData[props.data.options.deckName].cards);
      }
      const { animationData, ...newData } = props.data;
      for (let optionName in allVariables) {
        let selection = [];
        for (let option of allVariables[optionName]) {
          selection.push(<option value={option}>{option}</option>);
        }
        options.push(
          <tr>
            <td>{optionName}</td>
            <td>
              <select
                name={optionName}
                onChange={optionSelectCallBack.bind(null, {
                  optionName,
                  componentId: props.data.id,
                  type: "",
                  __undone: false,
                  currTurn: 0,
                })}
                defaultValue={props.data.options[optionName]}
              >
                {selection}
              </select>
            </td>
          </tr>,
        );
      }
      return (
        <div
          className="component-variables"
          onClick={selectClick}
          style={{
            left: Math.min(
              gameSituationNode.offsetWidth - windowWidth - 50,
              contextOpen[0] - gameSituationNode.offsetLeft,
              (-gameSituationNode.getBoundingClientRect().left + window.innerWidth) / situationScale - windowWidth,
            ),
            top: Math.min(
              gameSituationNode.offsetHeight - windowHeight - 50,
              contextOpen[1] - gameSituationNode.offsetTop,
              (-gameSituationNode.getBoundingClientRect().top + window.innerHeight) / situationScale - windowHeight,
            ),
            height: windowHeight + "px",
            width: windowWidth + "px",
          }}
        >
          <h2>{props.data.id}</h2>
          <table>
            <tbody>{options}</tbody>
          </table>
          <button className="x-button" onClick={(evt) => setContextOpen(false)}>
            X
          </button>
          <ComponentComp
            data={newData}
            x={50}
            y={55}
            tooltipWrapRef={props.tooltipWrapRef}
            scaleMult={scaleMult}
            preview={true}
            width={windowWidth - 50}
            height={windowHeight - 150}
          />
          <button
            className="discard-button"
            onClick={discardComponentCallBack.bind(null, props.data, props.data.origin)}
          >
            Discard
          </button>
        </div>
      );
    }
    return (
      <gameSituationContext.Provider
        value={{
          componentClickCallBack,
          positionClickCallBack,
          discardComponentCallBack,
          optionSelectCallBack,
          addGameIconsToText,
          macroClickCallBack,
          getSelectedComponent,
          bankReveal: bankReveal as GameComponentForGame | null,
          selectedComponent: props.selectedComponent ?? null,
          displayTooltips,
        }}
      >
        <div className="hidden component-variables"></div>
      </gameSituationContext.Provider>
    );
  }, [
    bankReveal,
    componentClickCallBack,
    discardComponentCallBack,
    getSelectedComponent,
    macroClickCallBack,
    optionSelectCallBack,
    positionClickCallBack,
    addGameIconsToText,
    contextOpen,
    //props.logPreview
  ]);

  function selectClick(evt: React.UIEvent) {
    evt.stopPropagation();
  }

  if (componentSelectable) {
    wrapClassName += " selectable";
  }
  if (clickable) {
    wrapClassName += " clickable " + "highlight-" + props.clickable;
  }
  if (componentInfo.blocksEvents) {
    wrapClassName += " block-events";
  }
  if (getSelectedComponent && getSelectedComponent()?.id === props.data.id && props.data.id) {
    wrapClassName += " selected";
  }
  let aData = props.data.animationData;
  let startTransform = "";
  if (aData?.offsetY || aData?.offsetX) {
    let actualScale = props.parentScale ?? 1;
    startTransform =
      "translate(" +
      (aData.offsetX / actualScale + (aData.dx / actualScale || 0)) +
      "px, " +
      (aData.offsetY / actualScale + (aData.dy / actualScale || 0)) +
      "px)" +
      " scale(" +
      targetScale +
      ")";
  }

  const ComponentVars = useCallback(() => {
    if (contextOpen) {
      return createPortal(tooltipElement, props.tooltipWrapRef?.current as HTMLElement);
    }
    return <></>;
  }, [contextOpen, props.tooltipWrapRef, tooltipElement]);

  useEffect(() => {
    if (!editMode) {
      setContextOpen(false);
    }
  }, [editMode]);

  let clickables = [];
  let areas = componentInfo.clickableAreas;
  initialSetup(tableState);
  if (areas && !props.logPreview) {
    for (let c in areas) {
      let newArea = (
        <ClickableArea
          parentInfo={componentInfo}
          parentData={props.data}
          areaData={areas[c]}
          name={c}
          key={c}
        ></ClickableArea>
      );
      clickables.push(newArea);
    }
  }

  let hitbox = componentInfo.hitbox ?? componentInfo.clipPath;
  if (hitbox) {
    wrapClassName += " has-hitbox";
  }
  let hitboxEl = <></>;
  if (componentSelectable || clickable) {
    hitboxEl = (
      <div
        className="component-hitbox"
        onClick={componentClickCallBack?.bind(null, props.data)}
        style={{ clipPath: hitbox }} // falls back to none
        key={"hitbox"}
      ></div>
    );
  }
  initialSetup(tableState);
  let tooltipContent: string | React.ReactNode = "";
  if (typeof customTooltip === "function") {
    try {
      tooltipContent = addGameIconsToText(customTooltip(props.data)) ?? "";
    } catch (error: any) {
      tooltipContent = "Error:" + error.message;
    }
  }
  let componentInner = (
    <div
      className={"component-inner " + innerClassName}
      style={innerStyle}
      onMouseOver={props.preview ? () => null : hoverCallBack}
      onMouseLeave={props.preview ? () => null : leaveCallBack}
    >
      {hitboxEl}
      {componentDisplay}
      {clickables}

      {innerElement}
    </div>
  );
  if (displayTooltips && tooltipContent) {
    componentInner = (
      <Tooltip
        title={displayTooltips ? tooltipContent : ""}
        enterDelay={1500}
        arrow
        enterNextDelay={1500}
        slotProps={{
          popper: {
            modifiers: [
              {
                name: "offset",
                options: {
                  offset: [0, 15],
                },
              },
            ],
          },
        }}
      >
        {componentInner}
      </Tooltip>
    );
  }
  let result = (
    <div
      className={wrapClassName}
      style={Object.assign(style, {})}
      onClick={props.preview || hitbox ? () => null : componentClickCallBack?.bind(null, props.data)}
      onContextMenu={
        props.preview || !editMode
          ? () => {
              if (!editMode) {
                eventManager.createAlert("Hold alt/option to use zoom lens", "green");
              }
            }
          : (evt) => {
              console.log("context", evt);
              if (contextOpen) {
                setContextOpen(false);
              } else {
                let gameSituationBound = document.querySelector(".game-situation")?.getBoundingClientRect();
                setContextOpen([
                  (evt.pageX - (gameSituationBound?.x ?? 0)) / situationScale,
                  (evt.pageY - (gameSituationBound?.y ?? 0)) / situationScale,
                ]);
              }
              evt.stopPropagation();
              evt.preventDefault();
            }
      }
      data-id={props.data.id}
      id={domId}
    >
      {componentInner}
      <ComponentVars />
    </div>
  );
  let animData = { ...props.data.animationData };
  if (props.displayMode === "opponentCardBack" || props.data?.options?.cardFlipped) {
    animData.targetFlipped = false;
  } else {
    animData.targetFlipped = true;
  }
  if (animData) {
    if (+evalSpeed) {
      animData.duration /= +evalSpeed;
    }
    setTimeout(() => {
      let targetElement = document.getElementById(domId);
      if (!targetElement) {
        return;
      }
      targetElement.style.display = "block";
      if (startTransform !== "" && targetTransform !== "") {
        if (animationPlayed === animData.timestamp) {
          return;
        }
        setAnimationPlayed(animData.timestamp);
        if (targetElement && Date.now() - animData.timestamp < 1000) {
          let animPhase = (Date.now() - animData.timestamp) / animData.duration;
          targetElement.style.display = "block";
          let startStyle = { transform: startTransform };
          let endStyle = { transform: targetTransform };
          if (animData.flipped) {
            let flipWrap = targetElement.querySelector(".card-flip-wrap");
            if (flipWrap) {
              if (animData.targetFlipped) {
                flipWrap.animate([{ transform: "rotateY(180deg)" }, { transform: "rotateY(0deg)" }], {
                  duration: animData.duration,
                  easing: "ease",
                });
              }
            }
          }
          targetElement.animate([startStyle, endStyle], {
            duration: animData.duration,
            easing: "ease",
            //fill: "forwards",
          }); //.style.transform = targetTransform;
        }
      }
    }, 0);
  }
  return result;
}
